import { useState } from "react";
import { toast } from "react-toastify";

import { InvestmentData } from "components/Onboarding/Steps";
import { useActionHook, useNavigate } from "hooks";
import { useUserActions, useViewActions } from "store";
import { OnboardingAnswers, OnboardingDealData } from "store/types/user";

interface OnboardingState {
  answers: OnboardingAnswers;
  investments: InvestmentData[];
  skipped: boolean;
}

// Helper to format characteristics with proper IDs for server validation
function buildCharacteristicData(
  characteristics: Array<{ characteristic_id: number; value: string; currency?: string | null }>
) {
  return characteristics
    .filter((cd) => cd.value)
    .map((cd, index) => ({
      id: `new_${index}`,
      deal_id: 0,
      characteristic_id: cd.characteristic_id,
      value: cd.value,
      currency: cd.currency || null,
      is_default: true,
      percentage: 100,
      is_deleted: false,
      investor_id: null,
      round_id: null
    }));
}

export const useOnboardingSubmit = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { submitOnboardingAction } = useUserActions();
  const { setSidebarStateAction } = useViewActions();
  const { action: submit } = useActionHook(submitOnboardingAction, {
    onSuccess: () => {
      navigate({ route: "myDeals" });
      setSidebarStateAction(true);
    },
    onReject: () => {
      toast.error("Failed to save onboarding data. Please try again.");
    }
  });

  const submitOnboarding = async (state: OnboardingState, accountHasInvestments: boolean): Promise<void> => {
    setIsSubmitting(true);

    try {
      // Extract data from dynamic answers structure
      const orgAnswers = state.answers.organization || {};
      const focusAnswers = state.answers.user_focus || {};
      const interestsAnswers = state.answers.user_interests || {};

      // Collect all answers into flat arrays
      const focusAreas: string[] = [];
      Object.values(focusAnswers).forEach((answer) => {
        if (Array.isArray(answer)) {
          focusAreas.push(...answer);
        } else if (answer) {
          focusAreas.push(answer);
        }
      });

      const assetClasses: string[] = [];
      Object.values(interestsAnswers).forEach((answer) => {
        if (Array.isArray(answer)) {
          assetClasses.push(...answer);
        } else if (answer) {
          assetClasses.push(answer);
        }
      });

      // Helper to ensure string value (not array)
      const toString = (value: string | string[] | undefined): string | null => {
        if (!value) return null;
        return Array.isArray(value) ? value[0] || null : value;
      };

      const basePayload = {
        type_of_organization: toString(orgAnswers[0]), // First question: org type
        investments_amount: toString(orgAnswers[1]), // Second question: portfolio size
        user_focus: focusAreas,
        user_interests: assetClasses
      };

      // Helper to create FormData with base fields
      const createFormData = () => {
        const formData = new FormData();
        if (basePayload.type_of_organization) {
          formData.append("type_of_organization", basePayload.type_of_organization);
        }
        if (basePayload.investments_amount) {
          formData.append("investments_amount", basePayload.investments_amount);
        }
        formData.append("user_focus", JSON.stringify(basePayload.user_focus));
        formData.append("user_interests", JSON.stringify(basePayload.user_interests));
        return formData;
      };

      // Second user: only send answers, deals already exist
      if (accountHasInvestments) {
        const formData = createFormData();
        formData.append("deals", JSON.stringify([]));
        await submit(formData);
        return;
      }

      // First user: Add deals data
      if (state.skipped || !state.investments.length) {
        // User skipped investment creation - use sample data (server will create them automatically)
        const formData = createFormData();
        formData.append("deals", JSON.stringify([]));
        await submit(formData);
      } else {
        // User created investments - format them similar to createDealAction
        const deals = state.investments.map(
          (inv, index): OnboardingDealData & { geography?: string; file_index?: number } => {
            const dealData: OnboardingDealData & { geography?: string; file_index?: number } = {
              title: inv.dealName,
              asset_class_id: inv.assetClass?.id!,
              currency: inv.currency
            };

            // Add sub asset class if present
            if (inv.subAssetClass?.id) {
              dealData.sub_asset_class_id = inv.subAssetClass.id;
            }

            // Add geography if present (as a separate field for backend to handle)
            if (inv.geography) {
              dealData.geography = inv.geography;
            }

            // Add extracted characteristics if present
            if (inv.extractedData?.characteristics && inv.extractedData.characteristics.length > 0) {
              dealData.characteristic_data = buildCharacteristicData(inv.extractedData.characteristics);
            }

            // Mark which deals have files
            if (inv.file) {
              dealData.file_index = index;
            }

            return dealData;
          }
        );

        // Create FormData with deals
        const formData = createFormData();
        formData.append("deals", JSON.stringify(deals));

        // Add each file with index if present
        state.investments.forEach((inv, index) => {
          if (inv.file) {
            formData.append(`files[${index}]`, inv.file);
          }
        });

        await submit(formData);
      }
    } catch (error) {
      console.error("Onboarding submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submitOnboarding, isSubmitting };
};
